import { v4 as uuidv4 } from 'uuid'
import { BlockType } from '../../common/constants'

/**
 * Recursively traverse the builder tree and return the block that matches `id`.
 * @param {string} id - Identifier of the block to locate.
 * @param {Array} source - Root array representing the builder tree.
 * @returns {Object|null} The matching block if found, otherwise null.
 */
const findBlockById = (id, source) => {
    const findInContent = content => {
        if (!Array.isArray(content)) {
            return null
        }
        for (const item of content) {
            if (item?.id === id) {
                return item
            }
            if (item?.content) {
                const found = findInContent(item.content)
                if (found) {
                    return found
                }
            }
        }
        return null
    }

    return findInContent(source)
}

/**
 * Provide metadata (parent, index, containing array, block) for the block identified by `id`.
 * @param {string} id - Identifier of the block to inspect.
 * @param {Array} content - Array that may contain the block.
 * @param {Object|null} parent - Parent block for the current `content` scope.
 * @returns {{parent: Object|null, parentContent: Array, index: number, block: Object}|null}
 */
const findParentInfoById = (id, content, parent = null) => {
    if (!Array.isArray(content)) {
        return null
    }

    for (let i = 0; i < content.length; i++) {
        const item = content[i]
        if (item?.id === id) {
            return {
                parent,
                parentContent: content,
                index: i,
                block: item
            }
        }
        if (item?.content) {
            const result = findParentInfoById(id, item.content, item)
            if (result) {
                return result
            }
        }
    }

    return null
}

/**
 * Collect every block id inside `content` to avoid duplicates when cloning new blocks.
 * @param {Array} content - Builder tree content.
 * @param {Set<string>} acc - Accumulator for found ids.
 * @returns {Set<string>} A set containing every discovered id.
 */
const collectBlockIds = (content, acc = new Set()) => {
    if (!Array.isArray(content)) {
        return acc
    }

    content.forEach(item => {
        if (!item || typeof item !== 'object') {
            return
        }
        if (item.id) {
            acc.add(item.id)
        }
        if (Array.isArray(item.content) && item.content.length > 0) {
            collectBlockIds(item.content, acc)
        }
    })

    return acc
}

/**
 * Clone a block tree and assign brand-new ids to every node.
 * @param {Object} block - Block tree to duplicate.
 * @param {Set<string>} [usedIds] - Optional set of ids already present in the page.
 * @returns {Object} Cloned block tree with unique identifiers.
 */
const generateNewIds = (block, usedIds) => {
    if (!block) {
        return block
    }

    const ids = usedIds ?? new Set()
    const newBlock = { ...block }

    if (!newBlock.id || ids.has(newBlock.id)) {
        newBlock.id = uuidv4()
    }
    ids.add(newBlock.id)

    if (Array.isArray(newBlock.content) && newBlock.content.length > 0) {
        newBlock.content = newBlock.content.map(child => generateNewIds(child, ids))
    }

    return newBlock
}

const cloneTemplate = (template) => {
    if (template == null) {
        return template
    }
    if (typeof structuredClone === 'function') {
        return structuredClone(template)
    }
    try {
        return JSON.parse(JSON.stringify(template))
    }
    catch (e) {
        console.error('Error cloning template', e)
        return template
    }
}

const hasValidIds = (block, usedIds, seen = new Set()) => {
    if (Array.isArray(block)) {
        for (const item of block) {
            if (!hasValidIds(item, usedIds, seen)) {
                return false
            }
        }
        return true
    }

    if (!block || typeof block !== 'object') {
        return true
    }

    if (!block.id || seen.has(block.id) || (usedIds && usedIds.has(block.id))) {
        return false
    }

    seen.add(block.id)

    if (Array.isArray(block.content)) {
        for (const child of block.content) {
            if (!hasValidIds(child, usedIds, seen)) {
                return false
            }
        }
    }

    return true
}

const appendChildBlock = (parent, child) => {
    if (!parent || !child) {
        return
    }
    parent.content = Array.isArray(parent.content) ? parent.content : []
    parent.content.push(child)
}

/**
 * Recursively merge `source` fields into `target`, preserving nested structures.
 * @param {Object} target - Original object to mutate.
 * @param {Object} source - Object containing updated values.
 * @returns {Object} The mutated target object for chaining.
 */
const mergeDeep = (target, source) => {
    Object.entries(source).forEach(([key, value]) => {
        const current = target[key]
        const areObjects =
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            current &&
            typeof current === 'object' &&
            !Array.isArray(current)

        if (areObjects) {
            mergeDeep(current, value)
        } else {
            target[key] = value
        }
    })
    return target
}

/**
 * Create a new block under the parent identified by `parentId` and return the updated tree and metadata.
 * @param {string} parentId - Identifier of the block that will receive the new child.
 * @param {string} newBlockType - Type assigned to the new block.
 * @param {Array} source - Root array representing the builder tree.
 * @returns {{page: Array, meta: {blockId: string}}|Array} Wrapper containing the updated tree and new block id.
 */
const addBlock = (parentId, newBlockType, source) => {
    if (!Array.isArray(source)) {
        return source
    }

    const newBlock = {
        id: uuidv4(),
        type: newBlockType,
        style: {},
        content: []
    }

    if (newBlockType === BlockType.Section) {
        source.push(newBlock)
        return { page: source, meta: { blockId: newBlock.id } }
    }

    const parent = findBlockById(parentId, source)
    if (!parent) {
        throw new Error(`Parent block with id ${parentId} not found`)
    }

    parent.content = parent.content || []
    parent.content.push(newBlock)
    return { page: source, meta: { blockId: newBlock.id } }
}

/**
 * Insert a template block under `parentId`.
 * @param {string} parentId - Identifier of the destination block.
 * @param {Object} template - Block template to insert.
 * @param {Array} source - Root array representing the builder tree.
 * @returns {{page: Array, meta: {blockId: string}}|Array} Wrapper containing the updated tree and inserted block id.
 */
const addBlockTemplate = (parentId, template, source) => {
    console.log('addBlockTemplate', parentId, template)

    if (!Array.isArray(source) || !template) {
        return source
    }

    const existingIds = collectBlockIds(source)
    let preparedTemplate = cloneTemplate(template)

    if (!hasValidIds(preparedTemplate, existingIds)) {
        preparedTemplate = generateNewIds(preparedTemplate, new Set(existingIds))
    }

    if (preparedTemplate.type === BlockType.Section) {
        source.push(preparedTemplate)
        return { page: source, meta: { blockId: preparedTemplate.id } }
    }

    const parent = findBlockById(parentId, source)
    if (!parent) {
        throw new Error(`Parent block with id ${parentId} not found`)
    }

    parent.content = parent.content || []
    parent.content.push(preparedTemplate)
    return { page: source, meta: { blockId: preparedTemplate.id } }
}

/**
 * Remove the block with the provided `id` from the builder tree, if present.
 * @param {string} id - Identifier of the block to delete.
 * @param {Array} source - Root array representing the builder tree.
 * @returns {Array} The original builder tree instance without the deleted block.
 */
const deleteBlock = (id, source) => {
    const removeFromContent = content => {
        if (!Array.isArray(content)) {
            return false
        }
        for (let i = 0; i < content.length; i++) {
            const item = content[i]
            if (item?.id === id) {
                content.splice(i, 1)
                return true
            }
            if (item?.content && removeFromContent(item.content)) {
                return true
            }
        }
        return false
    }

    removeFromContent(source)
    return source
}

/**
 * Move the block identified by `id` one position up or down within its parent's content array.
 * @param {string} id - Identifier of the block to move.
 * @param {Array} source - Root array representing the builder tree.
 * @param {'up'|'down'} direction - Determines whether the block moves toward the start or end of the array.
 * @returns {Array} The builder tree with the block reordered if possible.
 */
const switchBlockPosition = (id, source, direction) => {
    if (!['up', 'down'].includes(direction)) {
        throw new Error(`Unsupported direction '${direction}'. Use 'up' or 'down'.`)
    }

    const match = findParentInfoById(id, source)
    if (!match || !Array.isArray(match.parentContent)) {
        return source
    }

    const { parentContent, index } = match
    const offset = direction === 'up' ? -1 : 1
    const targetIndex = index + offset

    if (targetIndex < 0 || targetIndex >= parentContent.length) {
        return source
    }

    ;[parentContent[index], parentContent[targetIndex]] = [parentContent[targetIndex], parentContent[index]]
    return source
}

const switchBlocksPosition = (ids, source, direction) => {
    if (!Array.isArray(source) || !Array.isArray(ids) || ids.length === 0) {
        return source
    }
    if (!['up', 'down'].includes(direction)) {
        throw new Error(`Unsupported direction '${direction}'. Use 'up' or 'down'.`)
    }

    const uniqueIds = []
    ids.forEach((id) => {
        if (id && !uniqueIds.includes(id)) {
            uniqueIds.push(id)
        }
    })

    if (uniqueIds.length === 0) {
        return source
    }

    const matches = uniqueIds
        .map((id) => findParentInfoById(id, source))
        .filter((info) => info && Array.isArray(info.parentContent))

    if (matches.length !== uniqueIds.length) {
        return source
    }

    const groupedByParent = new Map()
    matches.forEach((match) => {
        const key = match.parentContent
        const current = groupedByParent.get(key) ?? []
        current.push(match.index)
        groupedByParent.set(key, current)
    })

    const canMoveAll = Array.from(groupedByParent.entries()).every(([parentContent, indexes]) => {
        if (!Array.isArray(parentContent) || indexes.length === 0) {
            return false
        }

        const sorted = [...indexes].sort((a, b) => a - b)
        if (direction === 'up') {
            return sorted[0] > 0
        }
        return sorted[sorted.length - 1] < parentContent.length - 1
    })

    if (!canMoveAll) {
        return source
    }

    groupedByParent.forEach((indexes, parentContent) => {
        const sorted = [...indexes].sort((a, b) => a - b)

        if (direction === 'up') {
            sorted.forEach((index) => {
                ;[parentContent[index - 1], parentContent[index]] = [parentContent[index], parentContent[index - 1]]
            })
            return
        }

        sorted.reverse().forEach((index) => {
            ;[parentContent[index + 1], parentContent[index]] = [parentContent[index], parentContent[index + 1]]
        })
    })

    return source
}

/**
 * Return the first child block of the block identified by `id`, or the block itself if no children exist.
 * @param {string} id - Identifier of the reference block.
 * @param {Array} source - Root array representing the builder tree.
 * @returns {Object|null} Either the first child block, the original block, or null if the block is missing.
 */
const selectChild = (id, source) => {
    const block = findBlockById(id, source)
    if (!block) {
        return null
    }
    const children = Array.isArray(block.content) ? block.content : []
    return children.length > 0 ? children[0] : block
}

/**
 * Return the parent block of the block identified by `id`, or the block itself if it has no parent.
 * @param {string} id - Identifier of the reference block.
 * @param {Array} source - Root array representing the builder tree.
 * @returns {Object|null} Either the parent block, the original block, or null if the block is missing.
 */
const selectParent = (id, source) => {
    const info = findParentInfoById(id, source)
    if (!info) {
        return null
    }
    return info.parent || info.block
}

/**
 * Return the next sibling of the block identified by `id`, or the block itself when it has no next sibling.
 * @param {string} id - Identifier of the reference block.
 * @param {Array} source - Root array representing the builder tree.
 * @returns {Object|null} The next sibling, the original block, or null if the block cannot be found.
 */
const selectNextSibling = (id, source) => {
    const info = findParentInfoById(id, source)
    if (!info) {
        return null
    }
    const next = info.parentContent[info.index + 1]
    return next || info.block
}

/**
 * Return the previous sibling of the block identified by `id`, or the block itself when it has no previous sibling.
 * @param {string} id - Identifier of the reference block.
 * @param {Array} source - Root array representing the builder tree.
 * @returns {Object|null} The previous sibling, the original block, or null if the block cannot be found.
 */
const selectPrevSibling = (id, source) => {
    const info = findParentInfoById(id, source)
    if (!info) {
        return null
    }
    const prev = info.parentContent[info.index - 1]
    return prev || info.block
}

/**
 * Merge the provided `updates` object into the block identified by `id`.
 * @param {string} id - Identifier of the block to update.
 * @param {Array} source - Root array representing the builder tree.
 * @param {Object} updates - Plain object containing the properties to override.
 * @returns {Array} The builder tree with the block updated when found.
 */
const updateBlock = (id, source, updates) => {
    if (!updates || typeof updates !== 'object') {
        return source
    }
    const block = findBlockById(id, source)
    if (!block) {
        return source
    }
    mergeDeep(block, updates)
    return source
}

const CONTAINER_BLOCKS = new Set([
    BlockType.Row,
    BlockType.Column,
    BlockType.Carousel,
    BlockType.Collapsable
])

/**
 * Paste a block into the builder tree using the same rules as the interactive builder.
 * @param {string} targetId - Identifier of the destination block selected by the user.
 * @param {Object} blockToPaste - Block tree copied from the clipboard.
 * @param {Array} source - Root array representing the builder tree.
 * @returns {{page: Array, meta: {blockId: string}}|Array} Wrapper containing the updated tree and the id of the pasted block.
 */
const pasteBlock = (targetId, blockToPaste, source) => {
    if (!targetId || !blockToPaste || !Array.isArray(source)) {
        return source
    }

    const targetInfo = findParentInfoById(targetId, source)
    if (!targetInfo) {
        return source
    }

    const targetBlock = targetInfo.block
    if (!targetBlock) {
        return source
    }

    const ids = collectBlockIds(source)
    const blockClone = generateNewIds(blockToPaste, ids)

    if (blockClone.type === BlockType.Section) {
        source.push(blockClone)
        return { page: source, meta: { blockId: blockClone.id } }
    }

    if (targetBlock.type === BlockType.Section) {
        appendChildBlock(targetBlock, blockClone)
        return { page: source, meta: { blockId: blockClone.id } }
    }

    if (CONTAINER_BLOCKS.has(targetBlock.type)) {
        if (blockClone.type !== targetBlock.type) {
            appendChildBlock(targetBlock, blockClone)
            return { page: source, meta: { blockId: blockClone.id } }
        }
        const parent = targetInfo.parent
        if (parent && (parent.type === BlockType.Section || CONTAINER_BLOCKS.has(parent.type))) {
            appendChildBlock(parent, blockClone)
        }
        return { page: source, meta: { blockId: blockClone.id } }
    }

    const parent = targetInfo.parent
    if (parent && CONTAINER_BLOCKS.has(parent.type)) {
        appendChildBlock(parent, blockClone)
    }

    return { page: source, meta: { blockId: blockClone.id } }
}

/**
 * Toggle the visibility of a block for the specified view.
 * @param {string} id - Identifier of the block to modify.
 * @param {'desktop'|'tablet'|'mobile'} view - Viewport whose style should be toggled.
 * @param {Array} source - Root array representing the builder tree.
 * @returns {Array} The mutated builder tree after the toggle.
 */
const toggleHideBlock = (id, view, source) => {
    const block = findBlockById(id, source)
    if (!block) {
        return source
    }

    if (!view || view === 'desktop') {
        const nextDesktopDisplay = block.style?.display === 'none' ? '' : 'none'
        const nextUpdates = {
            style: {
                ...(block.style ?? {}),
                display: nextDesktopDisplay
            }
        }

        if (nextDesktopDisplay === 'none') {
            if (block.styleTablet?.display === undefined) {
                nextUpdates.styleTablet = {
                    ...(block.styleTablet ?? {}),
                    display: ''
                }
            }

            if (block.styleMobile?.display === undefined) {
                nextUpdates.styleMobile = {
                    ...(block.styleMobile ?? {}),
                    display: ''
                }
            }
        }

        updateBlock(id, source, nextUpdates)
    } else if (view === 'tablet') {
        const effectiveDisplay = block.styleTablet?.display ?? block.style?.display
        updateBlock(id, source, {
            styleTablet: {
                ...(block.styleTablet ?? {}),
                display: effectiveDisplay === 'none' ? '' : 'none'
            }
        })
    } else if (view === 'mobile') {
        const effectiveDisplay = block.styleMobile?.display ?? block.styleTablet?.display ?? block.style?.display
        updateBlock(id, source, {
            styleMobile: {
                ...(block.styleMobile ?? {}),
                display: effectiveDisplay === 'none' ? '' : 'none'
            }
        })
    }

    return source
}

export {
    findBlockById,
    addBlock,
    addBlockTemplate,
    deleteBlock,
    switchBlockPosition,
    switchBlocksPosition,
    selectChild,
    selectParent,
    selectNextSibling,
    selectPrevSibling,
    updateBlock,
    pasteBlock,
    toggleHideBlock
}
